/** @jsx jsx */

import { jsx, css } from '@emotion/core';

import {
  Button,
  ButtonGroup,
  Modal,
  ModalContent,
  Heading,
  Text,
  Spacer,
} from '@zapier/design-system';

import { ChangeType, PlanV2, Size } from '@zapier/checkout/src/types';
import { Confetti } from '@zapier/checkout/src/components/Confetti';
import { useSize } from '@zapier/checkout/src/hooks';

const modalContentWrapperStyles = (size: Size) => css`
  text-align: center;
  width: ${size === 'small' ? '100%' : '650px'};

  // Prevents the confetti from overflowing the modal
  & > div {
    overflow: hidden !important;
    position: relative !important;
  }
`;

const getHeadingText = (changeType: ChangeType) => {
  switch (changeType) {
    case 'upgrade':
      return 'Thanks for upgrading!';
    case 'downgrade':
      return 'Downgrade complete';
    case 'change':
      return 'Change complete';
    default:
      return '';
  }
};

const buildContent = ({
  changeType,
  newPlan,
}: {
  changeType: ChangeType;
  newPlan: PlanV2;
}) => {
  let planStatement = '';
  let payingStatement = '';

  if (newPlan.allowCompanies || newPlan.hasTeams) {
    planStatement = `You just created a ${newPlan.type}. Well done`;
  } else {
    planStatement = `You're now on the ${newPlan.name} plan`;
    payingStatement =
      newPlan.amount === 0 ? '' : `paying ${newPlan.interval}ly`;
  }
  const punctuation = changeType === 'upgrade' ? '!' : '.';
  return `${planStatement} ${payingStatement}${punctuation}`.replace(
    ` ${punctuation}`,
    punctuation
  );
};

const getPrimaryButtonProps = ({
  hasPausedZaps,
  newPlan,
}: {
  hasPausedZaps: boolean;
  newPlan: PlanV2;
}) => {
  if (newPlan.allowCompanies) {
    return {
      href: '/app/settings/organization',
      text: 'Start Setup',
    };
  }
  if (newPlan.hasTeams) {
    return {
      href: '/app/settings/members',
      text: 'Invite Team Members',
    };
  }
  if (hasPausedZaps) {
    return {
      href: '/app/zaps/folder/paused',
      text: 'View Paused Zaps',
    };
  }
  return {
    href: '/app/zaps',
    text: 'Go to My Zaps',
  };
};

const getSecondaryButtonProps = ({
  hasPausedZaps,
  newPlan,
}: {
  hasPausedZaps: boolean;
  newPlan: PlanV2;
}) => {
  if (newPlan.allowCompanies) {
    return {
      href: '/app/dashboard',
      text: 'Go to Dashboard',
    };
  }
  if (newPlan.hasTeams) {
    return {
      href: '/app/zaps',
      text: 'Go to Team Zaps',
    };
  }
  if (!hasPausedZaps) {
    return {
      href: '/app/billing/plans',
      text: '← Plans and Pricing',
    };
  }
  return null;
};

type Props = {
  /** The ChangeType for this plan change (`upgrade`, `downgrade` or `change`) */
  changeType: ChangeType;
  /** Indicates whether the plan change paused a user's zaps */
  hasPausedZaps?: boolean;
  /** The new plan the user has changed to */
  newPlan: PlanV2;
};

export const ChangePlanCompleteModal = ({
  changeType,
  hasPausedZaps = false,
  newPlan,
}: Props) => {
  const size = useSize();

  const heading = getHeadingText(changeType);
  const content = buildContent({
    changeType,
    newPlan,
  });

  const secondaryButtonProps = getSecondaryButtonProps({
    hasPausedZaps,
    newPlan,
  });

  const primaryButtonProps = getPrimaryButtonProps({
    hasPausedZaps,
    newPlan,
  });

  /**
   * TODO: Can we adjust Apollo caching policies in a way that doesn't require
   * us to do a full page refresh here?
   */
  const navigateTo = (href: string) => {
    window.location.href = href; // Hard page reload to pull in new plan data.
  };

  return (
    <Modal ariaLabel={heading} canClose={false} onClosed={() => null}>
      <div css={modalContentWrapperStyles(size)}>
        <ModalContent>
          {changeType === 'upgrade' && <Confetti />}

          <Heading>
            <Text type="pageHeader5">
              <span id="modal-title">{heading}</span>
            </Text>
          </Heading>

          <Spacer height={20} />

          <Text type="paragraph1Short">
            <span id="modal-description">{content}</span>
          </Text>

          <Spacer height={30} />

          <ButtonGroup
            columnSize={size === 'small' ? '100%' : 'minmax(0, 1fr)'}
          >
            {secondaryButtonProps && (
              <Button
                color="secondary"
                onClick={() => navigateTo(secondaryButtonProps.href)}
              >
                {secondaryButtonProps.text}
              </Button>
            )}

            <Button onClick={() => navigateTo(primaryButtonProps.href)}>
              {primaryButtonProps.text}
            </Button>
          </ButtonGroup>
        </ModalContent>
      </div>
    </Modal>
  );
};
